import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AGE_RANGES, STATUSES, TALENCE_LINKS, ZONES, DURATIONS } from "../lib/skills";
import { SKILLS_BY_CATEGORY, ALL_SKILLS } from "../lib/skills";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ChevronRight, Search, X } from "lucide-react";
import { useT } from "../lib/useLanguage";
import LogoSwap from "../components/LogoSwap";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

export default function ProfileEdit() {
  const t = useT();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ firstName: "", ageRange: "", status: "", talenceLink: "", zones: [], bio: "", skillsOffered: [], skillsNeeded: [], availability: "" });
  const [skillSearch, setSkillSearch] = useState("");

  const steps = [t("profile_step_basics"), t("profile_step_skills"), t("profile_step_zones"), t("profile_step_preview")];
  function upd(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function toggleZone(z) { upd("zones", form.zones.includes(z) ? form.zones.filter(x => x !== z) : [...form.zones, z]); }
  function toggleSkill(field, s) {
    const arr = form[field];
    if (arr.includes(s)) { upd(field, arr.filter(x => x !== s)); return; }
    if (arr.length >= 12) { toast.warning("Max 12 skills."); return; }
    upd(field, [...arr, s]);
  }

  const filteredSkills = skillSearch
    ? ALL_SKILLS.filter(s => s.toLowerCase().includes(skillSearch.toLowerCase()))
    : null;

  function handleSave() {
    if (!form.firstName || !form.ageRange || !form.status) { toast.error("Prénom, âge et statut sont requis."); return; }
    toast.success("Profil sauvegardé !");
    navigate("/profile");
  }

  const Chip = ({ label, active, onClick }) => (
    <button onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? "text-white border-transparent" : "bg-card border-border text-muted-foreground"}`}
      style={active ? { backgroundColor: "#FF6B4A", borderColor: "#FF6B4A" } : {}}>
      {label}
    </button>
  );

  return (
    <div className="max-w-lg mx-auto px-4 pt-4 pb-8">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => step > 0 ? setStep(s => s - 1) : navigate(-1)} className="p-1.5 rounded-full hover:bg-muted transition">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <LogoSwap size="xs" />
      </div>

      {/* Progress */}
      <div className="flex gap-1.5 mb-6">
        {steps.map((s, i) => (
          <div key={i} className="flex-1 h-1.5 rounded-full transition-all"
            style={{ backgroundColor: i <= step ? "#FF6B4A" : "#e5e7eb" }} />
        ))}
      </div>
      <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-1">{step + 1}/{steps.length}</p>
      <h1 className="text-xl font-extrabold mb-5">{steps[step]}</h1>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>

          {step === 0 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold">Prénom *</Label>
                <Input value={form.firstName} onChange={e => upd("firstName", e.target.value)} placeholder="Ton prénom" className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Tranche d'âge *</Label>
                <Select value={form.ageRange} onValueChange={v => upd("ageRange", v)}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Choisis" /></SelectTrigger>
                  <SelectContent>{AGE_RANGES.map(a => <SelectItem key={a} value={a}>{a} ans</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Statut *</Label>
                <Select value={form.status} onValueChange={v => upd("status", v)}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Choisis" /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Lien avec Talence *</Label>
                <Select value={form.talenceLink} onValueChange={v => upd("talenceLink", v)}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Choisis" /></SelectTrigger>
                  <SelectContent>{TALENCE_LINKS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Bio <span className="text-muted-foreground font-normal">(max 160 car.)</span></Label>
                <Textarea maxLength={160} value={form.bio} onChange={e => upd("bio", e.target.value)} placeholder="Ce que tu fais, ce que tu proposes..." className="rounded-xl mt-1 resize-none" rows={2} />
                <p className="text-xs text-muted-foreground mt-1 text-right">{form.bio.length}/160</p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input value={skillSearch} onChange={e => setSkillSearch(e.target.value)} placeholder="Chercher un skill..."
                  className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-xl text-sm focus:outline-none" />
              </div>

              {["skillsOffered", "skillsNeeded"].map(field => (
                <div key={field} className="mb-5">
                  <p className="text-xs font-bold uppercase tracking-wide mb-2 text-foreground">
                    {field === "skillsOffered" ? `Je propose (${form[field].length}/12)` : `Je cherche (${form[field].length}/12)`}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-2 min-h-[2rem]">
                    {form[field].map(s => (
                      <span key={s} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium"
                        style={field === "skillsOffered" ? { backgroundColor: "#B9F85A", color: "#101828" } : { backgroundColor: "#FF6B4A20", color: "#FF6B4A" }}>
                        {s} <button onClick={() => toggleSkill(field, s)}><X className="h-2.5 w-2.5" /></button>
                      </span>
                    ))}
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredSkills ? (
                      <div className="flex flex-wrap gap-1.5">
                        {filteredSkills.map(s => <Chip key={s} label={s} active={form[field].includes(s)} onClick={() => toggleSkill(field, s)} />)}
                      </div>
                    ) : (
                      Object.entries(SKILLS_BY_CATEGORY).map(([cat, skills]) => (
                        <div key={cat} className="mb-3">
                          <p className="text-xs font-semibold text-muted-foreground mb-1.5">{cat}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {skills.map(s => <Chip key={s} label={s} active={form[field].includes(s)} onClick={() => toggleSkill(field, s)} />)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wide mb-2">Zones préférées <span className="font-normal text-muted-foreground">(multi-sélection)</span></p>
              <p className="text-xs text-muted-foreground mb-3">Zone approximative uniquement. Jamais de position exacte.</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {ZONES.map(z => <Chip key={z} label={z} active={form.zones.includes(z)} onClick={() => toggleZone(z)} />)}
              </div>
              <div>
                <Label className="text-xs font-semibold">Disponibilité</Label>
                <Input value={form.availability} onChange={e => upd("availability", e.target.value)} placeholder="Ex: week-end, soir, flexible..." className="rounded-xl mt-1" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="bg-card rounded-2xl border border-border overflow-hidden mb-4">
                <div className="h-1.5" style={{ background: "linear-gradient(to right, #FF6B4A, #B9F85A)" }} />
                <div className="p-5">
                  <h3 className="text-xl font-extrabold">{form.firstName || "Ton prénom"}</h3>
                  <p className="text-sm text-muted-foreground">{form.ageRange} · {form.status}</p>
                  <p className="text-sm mt-3">{form.bio || "Ta bio ici."}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {form.skillsOffered.map(s => <span key={s} className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: "#B9F85A", color: "#101828" }}>{s}</span>)}
                  </div>
                </div>
              </div>
              <div className="rounded-xl p-3 text-xs text-muted-foreground space-y-1" style={{ backgroundColor: "#FF6B4A10" }}>
                <p>✓ Pas de téléphone public</p>
                <p>✓ Zone approximative uniquement</p>
                <p>✓ Photo seule visible — pas de genre affiché</p>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex gap-3 mt-6">
        {step < 3 ? (
          <Button onClick={() => setStep(s => s + 1)} className="flex-1 rounded-xl" size="lg" style={{ backgroundColor: "#FF6B4A" }}>
            Continuer <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={handleSave} className="flex-1 rounded-xl" size="lg" style={{ backgroundColor: "#FF6B4A" }}>
            {t("profile_save")}
          </Button>
        )}
      </div>
    </div>
  );
}